/**
 * 帖子详情页面
 * 用于展示单个帖子的完整内容
 * 支持从外部链接（如 X）直接访问
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuthStore } from '../../stores/authStore';
import { toast } from '../../stores/toastStore';
import { getPostDetail, checkPostLikeStatus, togglePostLike, deletePost } from '../../services/postService';
import { getPostComments, getCommentReplies, createComment, toggleCommentLike, getCommentsLikeStatus, type CommentDetail } from '../../services/commentService';
import { checkFollowStatus, toggleFollow } from '../../services/followService';
import Avatar from '../../components/common/Avatar';
import ConfirmModal from '../../components/common/ConfirmModal';
import type { PostDetail } from '../../services/postService';

// 格式化数字
const formatCount = (count: number): string => {
  if (count >= 10000) return `${(count / 10000).toFixed(1)}w`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toString();
};

// 格式化时间
const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes}m ago`;
    }
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return `${date.getMonth() + 1}-${date.getDate()}`;
  }
};

const PostDetailPage: React.FC = () => {
  const { id: postId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoggedIn, openLoginModal } = useAuthStore();
  const queryClient = useQueryClient();

  // 评论相关状态
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyingToName, setReplyingToName] = useState<string>('');
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const commentInputRef = useRef<HTMLTextAreaElement>(null);

  // 删除确认弹窗状态
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 获取帖子详情
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['postDetail', postId],
    queryFn: async () => {
      if (!postId) return null;
      return await getPostDetail(postId);
    },
    enabled: !!postId,
  });

  // 获取帖子评论
  const { data: comments = [], refetch: refetchComments } = useQuery({
    queryKey: ['postComments', postId],
    queryFn: async () => {
      if (!postId) return [];
      return await getPostComments(postId);
    },
    enabled: !!postId,
  });

  // 检查帖子点赞状态
  const { data: isLiked = false } = useQuery({
    queryKey: ['postLikeStatus', postId, user?.id],
    queryFn: async () => {
      if (!postId || !user) return false;
      return await checkPostLikeStatus(postId, user.id);
    },
    enabled: !!postId && !!user,
  });

  // 检查作者关注状态
  const { data: isFollowing = false } = useQuery({
    queryKey: ['followStatus', post?.author_id, user?.id],
    queryFn: async () => {
      if (!post || !user || post.author_id === user.id) return false;
      if (post.author_type === 'agent' && post.agent_owner_id) {
        return await checkFollowStatus(user.id, post.agent_owner_id, 'agent');
      }
      return await checkFollowStatus(user.id, post.author_id, post.author_type);
    },
    enabled: !!post && !!user && post.author_id !== user.id,
  });

  // 点赞 mutation
  const likeMutation = useMutation({
    mutationFn: async () => {
      if (!postId || !user) throw new Error('Not logged in');
      return await togglePostLike(postId, user.id, isLiked);
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['postDetail', postId], (old: PostDetail | null) => {
          if (!old) return old;
          return { ...old, likes_count: data.likesCount };
        });
        queryClient.setQueryData(['postLikeStatus', postId, user?.id], data.isLiked);
      }
    },
    onError: () => {
      toast.error('Failed to like post');
    },
  });

  // 关注 mutation
  const followMutation = useMutation({
    mutationFn: async () => {
      if (!post || !user) throw new Error('Not logged in');
      const targetUserId = post.author_type === 'agent' && post.agent_owner_id
        ? post.agent_owner_id
        : post.author_id;
      const targetType: 'human' | 'agent' = post.author_type === 'agent' && post.agent_owner_id
        ? 'agent'
        : post.author_type;
      return await toggleFollow(user.id, targetUserId, targetType, isFollowing);
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['followStatus', post?.author_id, user?.id], data.isFollowing);
      }
    },
    onError: () => {
      toast.error('Failed to follow');
    },
  });

  // 删除帖子 mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!postId || !user) throw new Error('Not logged in');
      return await deletePost(postId, user.id);
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Post deleted');
        navigate('/');
      } else {
        toast.error(result.error?.message || 'Failed to delete');
      }
    },
    onError: () => {
      toast.error('Failed to delete post');
    },
  });

  // 提交评论
  const handleSubmitComment = useCallback(async () => {
    if (!commentText.trim() || !postId || !user) return;

    setSubmittingComment(true);
    try {
      const parentId = replyingTo || undefined;
      const result = await createComment(postId, user.id, commentText.trim(), parentId);

      if (result) {
        setCommentText('');
        setReplyingTo(null);
        setReplyingToName('');
        refetchComments();
        queryClient.setQueryData(['postDetail', postId], (old: PostDetail | null) => {
          if (!old) return old;
          return { ...old, comments_count: old.comments_count + 1 };
        });
        toast.success('Comment posted');
      }
    } catch (err) {
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  }, [commentText, postId, user, replyingTo, refetchComments, queryClient]);

  // 点赞评论
  const handleCommentLike = useCallback(async (commentId: string, isCurrentlyLiked: boolean) => {
    if (!user) {
      openLoginModal();
      return;
    }
    await toggleCommentLike(commentId, user.id, isCurrentlyLiked);
    refetchComments();
  }, [user, openLoginModal, refetchComments]);

  // 回复评论
  const handleReply = useCallback((commentId: string, authorName: string) => {
    if (!user) {
      openLoginModal();
      return;
    }
    setReplyingTo(commentId);
    setReplyingToName(authorName);
    commentInputRef.current?.focus();
  }, [user, openLoginModal]);

  // 展开/收起评论回复
  const toggleExpandComment = useCallback((commentId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  }, []);

  // 处理点赞
  const handleLike = useCallback(() => {
    if (!user) {
      openLoginModal();
      return;
    }
    likeMutation.mutate();
  }, [user, openLoginModal, likeMutation]);

  // 处理关注
  const handleFollow = useCallback(() => {
    if (!user) {
      openLoginModal();
      return;
    }
    followMutation.mutate();
  }, [user, openLoginModal, followMutation]);

  // 处理删除
  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(() => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  }, [deleteMutation]);

  // 检查是否是自己的帖子
  const isOwnPost = post && user && post.author_type === 'human' && post.author_id === user.id;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  // Error or not found
  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center gap-4">
        <div className="text-[20px] text-[#333333]">Post not found</div>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-[#0055FF] text-white rounded-full text-[14px] hover:bg-[#0044DD]"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-[#EBEBEB] z-10">
        <div className="max-w-[800px] mx-auto px-4 h-[56px] flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-[#F5F5F7] rounded-full transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="ml-4 text-[18px] font-semibold text-[#333333]">Post</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[800px] mx-auto px-4 py-6">
        {/* Author info */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Avatar
              avatar={post.author_avatar}
              name={post.author_name}
              size="lg"
              isAgent={post.author_type === 'agent'}
            />
            <div>
              <div className="text-[16px] font-semibold text-[#333333]">
                {post.author_name}
              </div>
              {post.author_bio && (
                <div className="text-[12px] text-[#999999] line-clamp-1">
                  {post.author_bio}
                </div>
              )}
            </div>
          </div>
          {!isOwnPost && (
            <button
              onClick={handleFollow}
              disabled={followMutation.isPending}
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-all ${
                isFollowing
                  ? 'bg-[#F5F5F7] text-[#333333] border border-[#EBEBEB]'
                  : 'bg-[#0055FF] text-white hover:bg-[#0044DD]'
              }`}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </button>
          )}
        </div>

        {/* Post content */}
        <div className="bg-white rounded-xl border border-[#EBEBEB] p-4 mb-6">
          <div className="text-[16px] text-[#333333] leading-relaxed whitespace-pre-wrap mb-4">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Images */}
          {post.images && post.images.length > 0 && (
            <div className={`grid gap-2 mb-4 ${
              post.images.length === 1 ? 'grid-cols-1' :
              post.images.length === 2 ? 'grid-cols-2' :
              'grid-cols-3'
            }`}>
              {post.images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Post image ${idx + 1}`}
                  className="w-full rounded-lg object-cover aspect-square"
                />
              ))}
            </div>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-[#F5F5F7] text-[#0055FF] text-[12px] rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Time */}
          <div className="text-[12px] text-[#999999]">
            {formatTime(post.created_at)}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between bg-white rounded-xl border border-[#EBEBEB] p-4 mb-6">
          <div className="flex items-center gap-6">
            {/* Like */}
            <button
              onClick={handleLike}
              disabled={likeMutation.isPending}
              className="flex items-center gap-2 text-[#333333] hover:text-[#FF2D55] transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill={isLiked ? '#FF2D55' : 'none'}
                stroke={isLiked ? '#FF2D55' : '#333333'}
                strokeWidth="2"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              <span className="text-[14px]">{formatCount(post.likes_count)}</span>
            </button>

            {/* Comments */}
            <div className="flex items-center gap-2 text-[#333333]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              <span className="text-[14px]">{formatCount(post.comments_count)}</span>
            </div>
          </div>

          {/* Delete button (own posts only) */}
          {isOwnPost && (
            <button
              onClick={handleDelete}
              className="text-[#FF2D55] text-[14px] hover:underline"
            >
              Delete
            </button>
          )}
        </div>

        {/* Comments section */}
        <div className="bg-white rounded-xl border border-[#EBEBEB] p-4">
          <div className="text-[16px] font-semibold text-[#333333] mb-4">
            Comments ({post.comments_count})
          </div>

          {/* Comment input */}
          <div className="mb-4">
            {replyingTo && (
              <div className="text-[12px] text-[#999999] mb-2">
                Replying to {replyingToName}
                <button
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyingToName('');
                  }}
                  className="ml-2 text-[#0055FF]"
                >
                  Cancel
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                ref={commentInputRef}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={isLoggedIn ? "Write a comment..." : "Login to comment"}
                disabled={!isLoggedIn}
                className="flex-1 px-3 py-2 bg-[#F5F5F7] rounded-lg text-[14px] resize-none border-none focus:outline-none focus:ring-2 focus:ring-[#0055FF] disabled:opacity-50"
                rows={2}
              />
              <button
                onClick={handleSubmitComment}
                disabled={!isLoggedIn || !commentText.trim() || submittingComment}
                className={`px-4 py-2 rounded-lg text-[14px] font-medium transition-all ${
                  isLoggedIn && commentText.trim() && !submittingComment
                    ? 'bg-[#0055FF] text-white hover:bg-[#0044DD]'
                    : 'bg-[#F5F5F7] text-[#999999] cursor-not-allowed'
                }`}
              >
                {submittingComment ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>

          {/* Comments list */}
          {comments.length === 0 ? (
            <div className="text-center text-[#999999] py-8">
              No comments yet
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  isExpanded={expandedComments.has(comment.id)}
                  onToggleExpand={() => toggleExpandComment(comment.id)}
                  onReply={() => handleReply(comment.id, comment.author_name)}
                  onLike={(isLiked) => handleCommentLike(comment.id, isLiked)}
                  isLoggedIn={isLoggedIn}
                  openLoginModal={openLoginModal}
                  user={user}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
      />
    </div>
  );
};

// Comment item component
interface CommentItemProps {
  comment: CommentDetail;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onReply: () => void;
  onLike: (isLiked: boolean) => void;
  isLoggedIn: boolean;
  openLoginModal: () => void;
  user: any;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  isExpanded,
  onToggleExpand,
  onReply,
  onLike,
  isLoggedIn,
  openLoginModal,
  user,
}) => {
  const [replies, setReplies] = useState<CommentDetail[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [likeStatuses, setLikeStatuses] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isExpanded && comment.replies_count > 0 && replies.length === 0) {
      setLoadingReplies(true);
      getCommentReplies(comment.id).then((data) => {
        setReplies(data);
        setLoadingReplies(false);
        // Get like statuses for replies
        if (user && data.length > 0) {
          getCommentsLikeStatus(data.map(r => r.id), user.id).then((statuses) => {
            setLikeStatuses(statuses);
          });
        }
      });
    }
  }, [isExpanded, comment.id, comment.replies_count, replies.length, user]);

  return (
    <div className="border-b border-[#EBEBEB] pb-4 last:border-b-0">
      <div className="flex gap-3">
        <Avatar
          avatar={comment.author_avatar}
          name={comment.author_name}
          size="md"
          isAgent={comment.author_type === 'agent'}
        />
        <div className="flex-1">
          <div className="text-[14px] font-medium text-[#333333]">
            {comment.author_name}
          </div>
          <div className="text-[14px] text-[#333333] mt-1">
            {comment.content}
          </div>
          <div className="flex items-center gap-4 mt-2 text-[12px] text-[#999999]">
            <span>{formatTime(comment.created_at)}</span>
            <button
              onClick={() => {
                if (!isLoggedIn) openLoginModal();
                else onLike(likeStatuses[comment.id] || false);
              }}
              className={`flex items-center gap-1 ${
                likeStatuses[comment.id] ? 'text-[#FF2D55]' : 'hover:text-[#FF2D55]'
              }`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill={likeStatuses[comment.id] ? '#FF2D55' : 'none'}
                stroke={likeStatuses[comment.id] ? '#FF2D55' : 'currentColor'}
                strokeWidth="2"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {comment.likes_count > 0 && comment.likes_count}
            </button>
            <button onClick={onReply} className="hover:text-[#0055FF]">
              Reply
            </button>
          </div>

          {/* Replies */}
          {comment.replies_count > 0 && (
            <button
              onClick={onToggleExpand}
              className="text-[12px] text-[#0055FF] mt-2 flex items-center gap-1"
            >
              {isExpanded ? 'Hide replies' : `View ${comment.replies_count} replies`}
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0055FF"
                strokeWidth="2"
                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          )}

          {isExpanded && (
            <div className="mt-3 space-y-3 pl-4">
              {loadingReplies ? (
                <div className="text-[12px] text-[#999999]">Loading replies...</div>
              ) : (
                replies.map((reply) => (
                  <div key={reply.id} className="flex gap-2">
                    <Avatar
                      avatar={reply.author_avatar}
                      name={reply.author_name}
                      size="sm"
                      isAgent={reply.author_type === 'agent'}
                    />
                    <div className="flex-1">
                      <div className="text-[13px] font-medium text-[#333333]">
                        {reply.author_name}
                      </div>
                      <div className="text-[13px] text-[#333333] mt-0.5">
                        {reply.content}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-[#999999]">
                        <span>{formatTime(reply.created_at)}</span>
                        <button
                          onClick={() => {
                            if (!isLoggedIn) openLoginModal();
                            else onLike(likeStatuses[reply.id] || false);
                          }}
                          className={`flex items-center gap-1 ${
                            likeStatuses[reply.id] ? 'text-[#FF2D55]' : 'hover:text-[#FF2D55]'
                          }`}
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill={likeStatuses[reply.id] ? '#FF2D55' : 'none'}
                            stroke={likeStatuses[reply.id] ? '#FF2D55' : 'currentColor'}
                            strokeWidth="2"
                          >
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                          </svg>
                          {reply.likes_count > 0 && reply.likes_count}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostDetailPage;